import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { getSavedLanguage } from '../utils/geolocation';
import '../App.tailwind.css';

// Language translations for Thank You page
const THANK_YOU_LANGUAGES = {
  en: {
    thankYou: 'Thank you',
    orderConfirmed: 'Your order is confirmed',
    confirmationEmail: "You'll receive a confirmation email with your order number shortly.",
    downloadTickets: 'Download your tickets',
    downloadTicketsDesc: 'Please download your tickets or transfer them to your guests.',
    customerInfo: 'Customer information',
    contactInfo: 'Contact information',
    paymentMethod: 'Payment method',
    billingAddress: 'Billing address',
    cardEnding: 'Card ending with',
    subtotal: 'Subtotal',
    taxes: 'Taxes',
    taxesIncluded: 'Included',
    total: 'Total',
    quantity: 'Quantity',
    transfer: 'Transfer',
    pdf: 'PDF',
    addToWallet: 'Add to Apple Wallet',
    downloadInvoice: 'Download Invoice',
    generatingInvoice: 'Generating invoice...',
    invoice: 'Invoice',
    orderNumber: 'Order Number',
    customer: 'Customer',
    date: 'Date',
    shippingAddress: 'Shipping Address',
    items: 'Items'
  },
  fr: {
    thankYou: 'Merci',
    orderConfirmed: 'Votre commande est confirmée',
    confirmationEmail: 'Vous recevrez bientôt un e-mail de confirmation avec votre numéro de commande.',
    downloadTickets: 'Téléchargez vos billets',
    downloadTicketsDesc: 'Veuillez télécharger vos billets ou les transférer à vos invités.',
    customerInfo: 'Informations client',
    contactInfo: 'Informations de contact',
    paymentMethod: 'Méthode de paiement',
    billingAddress: 'Adresse de facturation',
    cardEnding: 'Carte se terminant par',
    subtotal: 'Sous-total',
    taxes: 'Taxes',
    taxesIncluded: 'Incluses',
    total: 'Total',
    quantity: 'Quantité',
    transfer: 'Transférer',
    pdf: 'PDF',
    addToWallet: 'Ajouter à Apple Wallet',
    downloadInvoice: 'Télécharger la facture',
    generatingInvoice: 'Génération de la facture...',
    invoice: 'Facture',
    orderNumber: 'Numéro de commande',
    customer: 'Client',
    date: 'Date',
    shippingAddress: 'Adresse de livraison',
    items: 'Articles'
  },
  es: {
    thankYou: 'Gracias',
    orderConfirmed: 'Tu pedido está confirmado',
    confirmationEmail: 'Recibirás un correo de confirmación con tu número de pedido en breve.',
    downloadTickets: 'Descarga tus entradas',
    downloadTicketsDesc: 'Por favor descarga tus entradas o transfiérelas a tus invitados.',
    customerInfo: 'Información del cliente',
    contactInfo: 'Información de contacto',
    paymentMethod: 'Método de pago',
    billingAddress: 'Dirección de facturación',
    cardEnding: 'Tarjeta terminada en',
    subtotal: 'Subtotal',
    taxes: 'Impuestos',
    taxesIncluded: 'Incluidos',
    total: 'Total',
    quantity: 'Cantidad',
    transfer: 'Transferir',
    pdf: 'PDF',
    addToWallet: 'Añadir a Apple Wallet',
    downloadInvoice: 'Descargar factura',
    generatingInvoice: 'Generando factura...',
    invoice: 'Factura',
    orderNumber: 'Número de pedido',
    customer: 'Cliente',
    date: 'Fecha',
    shippingAddress: 'Dirección de envío',
    items: 'Artículos'
  },
  de: {
    thankYou: 'Danke',
    orderConfirmed: 'Ihre Bestellung ist bestätigt',
    confirmationEmail: 'Sie erhalten in Kürze eine Bestätigungs-E-Mail mit Ihrer Bestellnummer.',
    downloadTickets: 'Laden Sie Ihre Tickets herunter',
    downloadTicketsDesc: 'Bitte laden Sie Ihre Tickets herunter oder übertragen Sie sie an Ihre Gäste.',
    customerInfo: 'Kundeninformationen',
    contactInfo: 'Kontaktinformationen',
    paymentMethod: 'Zahlungsmethode',
    billingAddress: 'Rechnungsadresse',
    cardEnding: 'Karte endend mit',
    subtotal: 'Zwischensumme',
    taxes: 'Steuern',
    taxesIncluded: 'Inbegriffen',
    total: 'Gesamt',
    quantity: 'Menge',
    transfer: 'Übertragen',
    pdf: 'PDF',
    addToWallet: 'Zu Apple Wallet hinzufügen',
    downloadInvoice: 'Rechnung herunterladen',
    generatingInvoice: 'Rechnung wird erstellt...',
    invoice: 'Rechnung',
    orderNumber: 'Bestellnummer',
    customer: 'Kunde',
    date: 'Datum',
    shippingAddress: 'Lieferadresse',
    items: 'Artikel'
  }
};

// Define styles for PDF
const styles = StyleSheet.create({
  page: { padding: 30 },
  title: { fontSize: 24, marginBottom: 30 },
  section: { margin: 10, padding: 10 },
  row: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 },
  bold: { fontWeight: 'bold' },
});

// Invoice component
const Invoice = ({ orderNumber, customerName, shippingDetails, cartItems, total, language = 'en' }) => {
  const t = THANK_YOU_LANGUAGES[language];
  
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>{t.invoice}</Text>

        <View style={styles.section}>
          <Text style={styles.bold}>{t.orderNumber}: {orderNumber}</Text>
          <Text>{t.customer}: {customerName}</Text>
          <Text>{t.date}: {new Date().toLocaleDateString()}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.bold}>{t.shippingAddress}:</Text>
          <Text>{shippingDetails.address1}</Text>
          <Text>{shippingDetails.address2}</Text>
          <Text>{shippingDetails.city}, {shippingDetails.state}</Text>
          <Text>{shippingDetails.country}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.bold}>{t.items}:</Text>
          {cartItems.map((item, index) => (
            <View key={index} style={styles.row}>
              <Text>{item.title} x {item.quantity}</Text>
              <Text>${(item.price * item.quantity / 100).toFixed(2)}</Text>
            </View>
          ))}
        </View>

        <View style={styles.section}>
          <View style={styles.row}>
            <Text style={styles.bold}>{t.total}:</Text>
            <Text style={styles.bold}>${total}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const ThankYouPage = () => {
  const location = useLocation();
  const { paymentIntent, shippingDetails, cartItems, total, store } = location.state || {};
  const [language, setLanguage] = useState('en');

  const orderNumber = paymentIntent?.id || "N/A";
  const storeName = store || "N/A";
  const customerName = `${shippingDetails?.firstName} ${shippingDetails?.lastName}`;
  const t = THANK_YOU_LANGUAGES[language];

  // Load saved language on component mount
  useEffect(() => {
    const savedLanguage = getSavedLanguage();
    setLanguage(savedLanguage);
  }, []);

  return (
    <div className="thank-you-page">
      <div className="left-column">
        <h1>{storeName}</h1>
        <div className="order-confirmation">
          <span className="checkmark">✓</span>
          <div>
            <p>Order #{orderNumber}</p>
            <p className="thank-you">{t.thankYou} {shippingDetails?.firstName}!</p>
          </div>
        </div>

        <div className="info-box">
          <h2>{t.orderConfirmed}</h2>
          <p>{t.confirmationEmail}</p>
        </div>

        <div className="info-box">
          <h2>{t.downloadTickets}</h2>
          <p>{t.downloadTicketsDesc}</p>
        </div>

        <div className="tickets">
          {cartItems && cartItems.map((item, index) => (
            <div key={index} className="ticket">
              <div>
                <p>{customerName}</p>
                <p>{item.title}</p>
                <p>#{Math.random().toString().slice(2, 11)}</p>
              </div>
              <div className="ticket-actions">
                <button>{t.transfer}</button>
                <button>{t.pdf}</button>
                <button className="apple-wallet">{t.addToWallet}</button>
              </div>
            </div>
          ))}
        </div>

        <div className="customer-info">
          <h2>{t.customerInfo}</h2>
          <div className="info-columns">
            <div>
              <h3>{t.contactInfo}</h3>
              <p>{paymentIntent?.billing_details?.email || "Email not provided"}</p>
            </div>
            <div>
              <h3>{t.paymentMethod}</h3>
              <p>{t.cardEnding} {paymentIntent?.payment_method_details?.card?.last4 || "****"} - ${(total / 100).toFixed(2)}</p>
              <h3>{t.billingAddress}</h3>
              <p>{customerName}</p>
              <p>{shippingDetails?.address1}</p>
              <p>{shippingDetails?.address2}</p>
              <p>{shippingDetails?.city}, {shippingDetails?.state}</p>
              <p>{shippingDetails?.country}</p>
            </div>
          </div>
        </div>

        <div className="invoice-download">
          <PDFDownloadLink
            document={
              <Invoice
                orderNumber={orderNumber}
                customerName={customerName}
                shippingDetails={shippingDetails}
                cartItems={cartItems}
                total={(total / 100).toFixed(2)}
                language={language}
              />
            }
            fileName={`invoice_${orderNumber}.pdf`}
          >
            {({ blob, url, loading, error }) =>
              loading ? t.generatingInvoice : t.downloadInvoice
            }
          </PDFDownloadLink>
        </div>
      </div>

      <div className="right-column">
        <div className="cart-info">
          {cartItems && cartItems.map((item, index) => (
            <div key={index} className="cart-item">
              <img src={item.image} alt={item.title} />
              <div>
                <h3>{item.title}</h3>
                <p>{t.quantity}: {item.quantity}</p>
              </div>
              <p className="price">${(item.price / 100).toFixed(2)}</p>
            </div>
          ))}
          <div className="cart-summary">
            <div>
              <p>{t.subtotal}</p>
              <p>${(total / 100).toFixed(2)}</p>
            </div>
            <div>
              <p>{t.taxes}</p>
              <p>{t.taxesIncluded}</p>
            </div>
            <div className="total">
              <p>{t.total}</p>
              <p>USD ${(total / 100).toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPage;